import React, { useMemo, useState } from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useQuery } from '@tanstack/react-query';
import { Button, Modal, Separator, Typography } from '@notch-ordering/ui-components';
import { useManualInvoiceSyncMutation } from '@/ar/hooks/mutations/InvoiceMutations';
import { FETCH_SUPPLIER_CONFIGURATION_QUERY_KEY, useGetSupplierConfiguration } from '@/ar/hooks/queries/IntegrationsQuery.hook';
import { EConfigurationType, getConnectionStatus } from '@/ar/network/Bushwhack.network';
import { useInvoiceStore } from '@/ar/stores/InvoicesStore';
import { useSupplierStore } from '@/ar/stores/SupplierStore';
import { getIntegrationTypeForRoutePath } from '@/ar/pages/IntegrationsPage/IntegrationUtils';
import { FeatureFlags } from '@/constants/FeatureFlags';
import { isManualInvoiceSyncConfigValid, parseInvoiceDocumentNumbers, shouldCloseManualInvoiceSyncModal } from './ManualInvoiceSyncModal.utils';

export const ManualInvoiceSyncModal: React.FC = () => {
    const { modals, setModal } = useInvoiceStore();
    const { supplierLoginData } = useSupplierStore();
    const flags = useFlags<FeatureFlags>();
    const supplierID = supplierLoginData?.supplier_id ?? '';
    const isOpen = modals?.manualInvoiceSync;
    const [documentNumbersInput, setDocumentNumbersInput] = useState('');
    const manualInvoiceSyncMutation = useManualInvoiceSyncMutation();

    const { data: supplierConfigurationResponse } = useGetSupplierConfiguration(supplierID);
    const rutterConfiguration = useMemo(
        () => supplierConfigurationResponse?.results?.find((integration) => integration.ownerID === supplierID && integration.type === EConfigurationType.ArRutter),
        [supplierConfigurationResponse, supplierID],
    );

    const { data: connectionStatus } = useQuery(
        [FETCH_SUPPLIER_CONFIGURATION_QUERY_KEY, 'MANUAL_INVOICE_SYNC_CONNECTION_STATUS', rutterConfiguration?.id],
        () => getConnectionStatus(rutterConfiguration.id, getIntegrationTypeForRoutePath(rutterConfiguration.type)),
        { enabled: isOpen && !!rutterConfiguration?.id },
    );

    const isConnectionReady = !!rutterConfiguration?.id && connectionStatus?.isReady === true;
    const isFeatureEnabled = flags.syncRutterManualInvoicesByNumber;
    const isEligible = isFeatureEnabled && isManualInvoiceSyncConfigValid(rutterConfiguration, isConnectionReady);
    const parsedInvoiceNumbers = useMemo(() => parseInvoiceDocumentNumbers(documentNumbersInput), [documentNumbersInput]);
    const invoiceCount = parsedInvoiceNumbers.length;
    const isSubmitDisabled = invoiceCount === 0 || manualInvoiceSyncMutation.isLoading || !isEligible;

    const handleClose = (): void => {
        setModal('manualInvoiceSync', false, null);
        setDocumentNumbersInput('');
        manualInvoiceSyncMutation.reset();
    };

    const handleSubmit = (): void => {
        if (!supplierID || invoiceCount === 0 || !isEligible) {
            return;
        }

        const invoiceNumbers = [...parsedInvoiceNumbers];
        manualInvoiceSyncMutation.mutate({
            supplierId: supplierID,
            invoiceNumbers,
        }, {
            onSuccess: (response) => {
                if (!shouldCloseManualInvoiceSyncModal(invoiceNumbers, response)) {
                    return;
                }
                handleClose();
            },
        });
    };

    if (!isOpen || !isFeatureEnabled) {
        return null;
    }

    return <Modal isOpen={isOpen}
        title={<Typography className="m-0 pr-10 mb-1" weight="font-semibold" variant="LG-2">Sync invoices manually</Typography>}
        headerPadding="mt-0 mb-5"
        titleSeparatorDesktop={true}
        close={handleClose}
        modalSize="SMALL"
        desktopModalWidth="lg:w-[600px]">
        <div className="px-5 pt-5 pb-8">
            <div className="space-y-6">
                <div>
                    <Typography className="font-medium mb-1">Sync from accounting</Typography>
                    <Typography className="text-gray-600 mb-0">
                        One document number per line. Each invoice is synced into AR.
                    </Typography>
                </div>
                <div className="space-y-2">
                    <Typography className="text-gray-900 mb-0" weight="font-medium">
                        Invoice numbers
                    </Typography>
                    <textarea
                        autoFocus
                        value={documentNumbersInput}
                        onChange={(e): void => setDocumentNumbersInput(e.target.value)}
                        placeholder={'One per line — e.g.\nINV-1001\nINV-1002'}
                        disabled={manualInvoiceSyncMutation.isLoading}
                        rows={5}
                        spellCheck={false}
                        className="w-full min-h-[120px] rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 disabled:bg-gray-50 disabled:text-gray-500" />
                    <Typography className="text-xs text-gray-600">
                        Match your accounting system—one number per line. Paste a column or type them in; duplicates are skipped.
                    </Typography>
                    {invoiceCount > 0 && (
                        <Typography className="text-sm text-gray-700">
                            {invoiceCount === 1 ? '1 invoice will be synced.' : `${invoiceCount} invoices will be synced.`}
                        </Typography>
                    )}
                </div>
                {!isEligible && (
                    <Typography className="text-red-500">
                        Manual sync is unavailable because your accounting integration is not fully connected.
                    </Typography>
                )}
            </div>
        </div>
        <Separator/>
        <div className="px-5 py-4">
            <div className="flex justify-end gap-3">
                <Button
                    onClick={handleClose}
                    variant="TERTIARY_FILLED"
                    size="SMALL"
                    minWidth="w-auto">
                    Cancel
                </Button>
                <Button
                    loading={manualInvoiceSyncMutation.isLoading}
                    onClick={handleSubmit}
                    disabled={isSubmitDisabled}
                    variant="SECONDARY"
                    size="SMALL"
                    minWidth="w-auto">
                    {invoiceCount <= 1 ? 'Sync invoice' : `Sync ${invoiceCount} invoices`}
                </Button>
            </div>
        </div>
    </Modal>;
};
